class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isPointerDownInside = false;
    this.mobileMainSearchScrollY = null;
    this.productCatalogPromise = null;
    this.lastExploreSignature = '';
    this.initialResultsMarkup = this.predictiveSearchResults.innerHTML;
    this.updateResultsWrapperHeight = this.updateResultsWrapperHeight.bind(this);
    this.updateMobileSearchHeight = this.updateMobileSearchHeight.bind(this);

    this.setupEventListeners();
    window.addEventListener('resize', this.updateResultsWrapperHeight);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.updateMobileSearchHeight);
      window.visualViewport.addEventListener('scroll', this.updateMobileSearchHeight);
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.addEventListener('pointerdown', () => {
      this.isPointerDownInside = true;
    });

    document.addEventListener('pointerup', () => {
      setTimeout(() => {
        this.isPointerDownInside = false;
      }, 0);
    });

    const closeButton = this.querySelector('[data-search-results-close]');

    if (closeButton) {
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.searchTerm = '';

          instance.abortController.abort();
          instance.abortController = new AbortController();

          instance.close(true);
        });

        this.input.blur();
      });
    }

    /* Header/menu search X:
      clear query + close predictive state everywhere.
      The existing details-modal handler still closes the modal itself. */
    const headerCloseButton = this
      .closest('.search-modal__content')
      ?.querySelector('.search-modal__close-button');

    if (headerCloseButton) {
      headerCloseButton.addEventListener('click', () => {
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.searchTerm = '';

          instance.abortController.abort();
          instance.abortController = new AbortController();

          instance.close(true);
        });

        this.input.blur();
      });
    }

  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showEmptyState();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showEmptyState();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showEmptyState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      /* Keep search open while interacting inside it,
        including dragging the scrollbar */
      if (this.isPointerDownInside) return;

      /* Header/menu search */
      const searchDetails = this.closest('.header__search')?.querySelector('details');

      if (searchDetails) {
        if (!searchDetails.open) {
          this.close();
        }

        return;
      }

      /* Search Results page */
      const searchResultsContainer = this.closest('.template-search__search');

      if (searchResultsContainer) {
        const activeInside = searchResultsContainer.contains(document.activeElement);

        if (!activeInside && !this.isPointerDownInside) {
          this.close();
        }

        return;
      }

      /* Fallback */
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 0);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.showEmptyState();
      event.preventDefault();
      return;
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.supplementProductResults(searchTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!resultsSection) {
          throw new Error('Predictive search response did not contain the expected section.');
        }

        const resultsMarkup = resultsSection.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        this.supplementProductResults(searchTerm);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  getProductCatalog() {
    if (this.productCatalogPromise) return this.productCatalogPromise;

    const shopRoot = window.Shopify?.routes?.root || '/';
    const catalogUrl = `${shopRoot}products.json?limit=250`;

    this.productCatalogPromise = fetch(catalogUrl, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Product catalog request failed: ${response.status}`);
        }

        return response.json();
      })
      .then((data) => (Array.isArray(data?.products) ? data.products : []))
      .catch((error) => {
        console.warn('Predictive search fallback could not load the product catalog.', error);
        return [];
      });

    return this.productCatalogPromise;
  }

  normalizeFallbackSearchText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  }

  async supplementProductResults(searchTerm) {
    const normalizedTerm = this.normalizeFallbackSearchText(searchTerm);

    if (!normalizedTerm) return;

    const products = await this.getProductCatalog();

    if (this.getQuery() !== searchTerm || !products.length) return;

    const shopRoot = window.Shopify?.routes?.root || '/';

    const existingProductPaths = new Set(
      Array.from(
        this.querySelectorAll('#predictive-search-results-products-list a[href*="/products/"]')
      ).map((link) => {
        try {
          return new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
        } catch {
          return link.getAttribute('href')?.replace(/\/$/, '') || '';
        }
      })
    );

    const matches = products
      .filter((product) => {
        const normalizedTitle = this.normalizeFallbackSearchText(product.title);

        if (!normalizedTitle) return false;

        return (
          normalizedTitle.startsWith(normalizedTerm) ||
          normalizedTitle.split(' ').some((word) => word.startsWith(normalizedTerm))
        );
      })
      .filter((product) => {
        const pathname = `${shopRoot}products/${product.handle}`
          .replace(/\/+/g, '/')
          .replace(/\/$/, '');

        return !existingProductPaths.has(pathname);
      })
      .slice(0, 6);

    if (!matches.length) return;

    const root = this.querySelector('#predictive-search-results');

    if (!root) return;

    let wrapper = this.querySelector('#predictive-search-results-groups-wrapper');

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.id = 'predictive-search-results-groups-wrapper';
      wrapper.className =
        'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';

      const loadingState = root.querySelector('.predictive-search__loading-state');
      root.insertBefore(wrapper, loadingState || root.firstChild);
    }

    wrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');

    let productList = wrapper.querySelector('#predictive-search-results-products-list');
    let productGroup = productList?.closest('.predictive-search__result-group');

    if (!productList) {
      const groups = Array.from(
        wrapper.querySelectorAll(':scope > .predictive-search__result-group')
      );

      productGroup = groups.length > 1 ? groups[groups.length - 1] : null;

      if (!productGroup) {
        productGroup = document.createElement('div');
        productGroup.className = 'predictive-search__result-group';
        wrapper.appendChild(productGroup);
      }

      const productSection = document.createElement('div');

      const heading = document.createElement('h2');
      heading.id = 'predictive-search-products';
      heading.className =
        'predictive-search__heading text-body caption-with-letter-spacing';
      heading.textContent = 'Products';

      productList = document.createElement('ul');
      productList.id = 'predictive-search-results-products-list';
      productList.className = 'predictive-search__results-list list-unstyled';
      productList.setAttribute('role', 'group');
      productList.setAttribute('aria-labelledby', 'predictive-search-products');

      productSection.append(heading, productList);
      productGroup.insertBefore(productSection, productGroup.firstChild);
    }

    let currentIndex =
      productList.querySelectorAll('.predictive-search__list-item').length;

    matches.forEach((product) => {
      currentIndex += 1;

      const item = document.createElement('li');
      item.id = `predictive-search-option-product-fallback-${currentIndex}`;
      item.className = 'predictive-search__list-item';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');

      const link = document.createElement('a');
      link.href = `${shopRoot}products/${product.handle}`.replace(/\/+/g, '/');
      link.className =
        'predictive-search__item predictive-search__item--link-with-thumbnail link link--text';
      link.tabIndex = -1;

      const imageSource = product.images?.[0]?.src;

      if (imageSource) {
        const image = document.createElement('img');
        image.className = 'predictive-search__image';
        image.src = imageSource;
        image.alt = product.title || '';
        image.width = 50;
        image.height = 50;
        image.loading = 'lazy';
        link.appendChild(image);
      }

      const content = document.createElement('div');
      content.className =
        'predictive-search__item-content predictive-search__item-content--centered';

      const title = document.createElement('p');
      title.className = 'predictive-search__item-heading h5';
      title.textContent = String(product.title || '').replace(/\|/g, ' ');

      content.appendChild(title);
      link.appendChild(content);
      item.appendChild(link);
      productList.appendChild(item);
    });

    if (window.matchMedia('(max-width: 749px)').matches) {
      requestAnimationFrame(this.updateMobileSearchHeight);
    } else {
      this.updateResultsWrapperHeight();
    }
  }

  getExploreCollections() {
    const source = this.querySelector('[data-search-explore-collections]');

    if (!source) return [];

    try {
      const collections = JSON.parse(source.textContent || '[]');

      return Array.isArray(collections)
        ? collections.filter((collection) => collection?.title && collection?.url)
        : [];
    } catch (error) {
      console.warn('Search Explore collections could not be read.', error);
      return [];
    }
  }

  getRandomExploreItem(items) {
    if (!Array.isArray(items) || !items.length) return null;

    return items[Math.floor(Math.random() * items.length)];
  }

  shuffleExploreItems(items) {
    const shuffled = [...items];

    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }

  async randomizeExplore() {
    const exploreList = this.querySelector('[data-search-explore-list]');

    if (!exploreList || this.getQuery().length) return;

    const searchUrl = window.routes?.search_url || '/search';
    const shopRoot = window.Shopify?.routes?.root || '/';

    const tagItems = [
      {
        key: 'tag:t-shirt',
        type: 'category',
        label: 'Section · T-shirts',
        url: `${searchUrl}?q=tag%3At-shirt&type=product`,
      },
      {
        key: 'tag:sweatshirts',
        type: 'category',
        label: 'Section · Sweatshirts',
        url: `${searchUrl}?q=tag%3Asweatshirts&type=product`,
      },
      {
        key: 'tag:hoodies',
        type: 'category',
        label: 'Section · Hoodies',
        url: `${searchUrl}?q=tag%3Ahoodies&type=product`,
      },
    ];

    const collectionItems = this.getExploreCollections().map((collection) => ({
      key: `collection:${collection.url}`,
      type: 'collection',
      label: `Collection · ${String(collection.title)}`,
      url: String(collection.url),
    }));

    const products = await this.getProductCatalog();

    /* The user may have started typing while the product catalogue loaded. */
    if (this.getQuery().length) return;

    const currentExploreList = this.querySelector('[data-search-explore-list]');

    if (!currentExploreList || currentExploreList !== exploreList) return;

    const productItems = products
      .filter((product) => product?.title && product?.handle)
      .map((product) => {
        const productTags = Array.isArray(product.tags)
          ? product.tags.map((tag) => String(tag).toLowerCase())
          : String(product.tags || '')
              .split(',')
              .map((tag) => tag.trim().toLowerCase())
              .filter(Boolean);

        let productIconType = 't-shirt';
        let productGarmentLabel = 'T-shirt';

        if (productTags.includes('hoodies')) {
          productIconType = 'hoodie';
          productGarmentLabel = 'Hoodie';
        } else if (productTags.includes('sweatshirts')) {
          productIconType = 'sweatshirt';
          productGarmentLabel = 'Sweatshirt';
        } else if (productTags.includes('t-shirt')) {
          productIconType = 't-shirt';
          productGarmentLabel = 'T-shirt';
        }

        const productTitle = String(product.title).replace(/\|/g, ' ').trim();
        const formattedProductTitle =
          productTitle.charAt(0).toUpperCase() + productTitle.slice(1);

        return {
          key: `product:${product.handle}`,
          type: 'product',
          productIconType,
          label: `Product · ${formattedProductTitle} | ${productGarmentLabel}`,
          url: `${shopRoot}products/${product.handle}`.replace(/\/+/g, '/'),
        };
      });

    if (!tagItems.length || !collectionItems.length || !productItems.length) return;

    let selected = [];
    let signature = '';

    /* Keep the result types in a fixed order: Collection → Section → Product.
       Try a few times so reopening search does not show the exact same trio. */
    for (let attempt = 0; attempt < 8; attempt += 1) {
      selected = [
        this.getRandomExploreItem(collectionItems),
        this.getRandomExploreItem(tagItems),
        this.getRandomExploreItem(productItems),
      ].filter(Boolean);

      signature = selected.map((item) => item.key).join('|');

      if (signature !== this.lastExploreSignature) break;
    }

    this.lastExploreSignature = signature;
    currentExploreList.replaceChildren();

    selected.forEach((exploreItem) => {
      const item = document.createElement('li');
      item.className = 'predictive-search__list-item';

      const link = document.createElement('a');
      link.href = exploreItem.url;
      link.className = 'predictive-search__item link link--text';

      const content = document.createElement('div');
      content.className =
        'predictive-search__item-content predictive-search__item-content--centered';
      content.style.display = 'flex';
      content.style.alignItems = 'center';
      content.style.gap = '0.8rem';

      const title = document.createElement('p');
      title.className = 'predictive-search__item-heading h5';
      title.textContent = exploreItem.label;

      content.appendChild(title);
      link.appendChild(content);
      item.appendChild(link);
      currentExploreList.appendChild(item);
    });

    if (window.matchMedia('(max-width: 749px)').matches) {
      requestAnimationFrame(this.updateMobileSearchHeight);
    } else {
      this.updateResultsWrapperHeight();
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  enterMobileMainSearchMode() {
    if (
      !window.matchMedia('(max-width: 749px)').matches ||
      !this.closest('.template-search__search')
    ) {
      return;
    }

    if (this.mobileMainSearchScrollY === null) {
      this.mobileMainSearchScrollY = window.scrollY;
    }

    document.documentElement.classList.add('search-results-mobile-active');
    document.body.classList.add('search-results-mobile-active');

    /* Put the actual search field at the top of the page.
       The Search Results title above it becomes irrelevant while search is active. */
    const field = this.querySelector('.field') || this.input;

    if (field) {
      const fieldTop = field.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: Math.max(0, Math.round(fieldTop)),
        left: 0,
        behavior: 'auto'
      });
    }
  }

  exitMobileMainSearchMode() {
    if (!this.closest('.template-search__search')) return;

    document.documentElement.classList.remove('search-results-mobile-active');
    document.body.classList.remove('search-results-mobile-active');

    if (this.mobileMainSearchScrollY === null) return;

    const restoreY = this.mobileMainSearchScrollY;
    this.mobileMainSearchScrollY = null;

    requestAnimationFrame(() => {
      window.scrollTo({
        top: restoreY,
        left: 0,
        behavior: 'auto'
      });
    });
  }

  lockMobileSearchScroll() {
    if (
      window.matchMedia('(max-width: 749px)').matches &&
      this.closest('.template-search__search')
    ) {
      document.documentElement.classList.add('search-results-scroll-locked');
      document.body.classList.add('search-results-scroll-locked');
    }
  }

  unlockMobileSearchScroll() {
    if (this.closest('.template-search__search')) {
      document.documentElement.classList.remove('search-results-scroll-locked');
      document.body.classList.remove('search-results-scroll-locked');
    }
  }

  updateMobileSearchHeight() {
    if (
      !window.matchMedia('(max-width: 749px)').matches ||
      !this.predictiveSearchResults ||
      !this.isOpen
    ) {
      return;
    }

    const viewport = window.visualViewport;
    const viewportBottom = viewport
      ? viewport.offsetTop + viewport.height
      : window.innerHeight;

    const panelRect = this.predictiveSearchResults.getBoundingClientRect();
    const safeGap = 8;
    const availablePanelHeight = Math.max(
      0,
      Math.floor(viewportBottom - panelRect.top - safeGap)
    );

    /* Auto height until content reaches the actually visible mobile viewport.
       setProperty(..., 'important') is intentional because mobile CSS has
       important search sizing rules. */
    this.predictiveSearchResults.style.setProperty(
      'max-height',
      `${availablePanelHeight}px`,
      'important'
    );

    const wrapper = this.querySelector('.predictive-search__results-groups-wrapper');
    const footer = this.querySelector('.predictive-search__search-for-button');

    if (!wrapper) {
      /* Empty state: let the whole panel scroll only if it exceeds the viewport. */
      this.predictiveSearchResults.style.setProperty('overflow-y', 'auto', 'important');
      this.predictiveSearchResults.style.setProperty(
        'overscroll-behavior',
        'contain',
        'important'
      );
      return;
    }

    /* Typed results: keep the footer visible and scroll only the results area. */
    this.predictiveSearchResults.style.setProperty('overflow-y', 'hidden', 'important');

    const wrapperRect = wrapper.getBoundingClientRect();
    const footerHeight = footer?.getBoundingClientRect().height || 0;

    const availableWrapperHeight = Math.max(
      0,
      Math.floor(viewportBottom - wrapperRect.top - footerHeight - safeGap)
    );

    wrapper.style.setProperty('max-height', `${availableWrapperHeight}px`, 'important');
    wrapper.style.setProperty('overflow-y', 'auto', 'important');
    wrapper.style.setProperty('overscroll-behavior', 'contain', 'important');
  }

  showEmptyState() {
    if (!this.initialResultsMarkup.trim()) return;

    this.searchTerm = '';
    this.predictiveSearchResults.innerHTML = this.initialResultsMarkup;
    this.randomizeExplore();
    this.removeAttribute('results');
    this.removeAttribute('loading');

    if (!window.matchMedia('(max-width: 749px)').matches) {
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    }

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    this.enterMobileMainSearchMode();
    this.lockMobileSearchScroll();

    if (window.matchMedia('(max-width: 749px)').matches) {
      requestAnimationFrame(this.updateMobileSearchHeight);
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const resultCount = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (resultCount) {
      this.setLiveRegionText(resultCount.textContent);
    }
  }

  updateResultsWrapperHeight() {
    const wrapper = this.querySelector('.predictive-search__results-groups-wrapper');
    const footer = this.querySelector('.predictive-search__search-for-button');

    if (!wrapper || !this.predictiveSearchResults || !this.isOpen) return;

    /* Mobile stays content-driven, but is capped to the actually visible
       browser viewport so browser chrome cannot cover the results. */
    if (window.matchMedia('(max-width: 749px)').matches) {
      this.updateMobileSearchHeight();
      return;
    }

    if (!footer) return;

    wrapper.style.removeProperty('max-height');

    requestAnimationFrame(() => {
      const resultsRect = this.predictiveSearchResults.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();

      const availableHeight = Math.max(
        0,
        resultsRect.bottom - wrapperRect.top - footerRect.height
      );

      wrapper.style.setProperty('max-height', `${availableHeight}px`);
      wrapper.style.setProperty('overflow-y', 'auto');
    });
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!window.matchMedia('(max-width: 749px)').matches) {
      this.predictiveSearchResults.style.maxHeight =
        this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    this.enterMobileMainSearchMode();
    this.lockMobileSearchScroll();

    if (window.matchMedia('(max-width: 749px)').matches) {
      requestAnimationFrame(this.updateMobileSearchHeight);
    } else {
      this.updateResultsWrapperHeight();
    }
  }

  close(clearSearchTerm = true) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;

    this.unlockMobileSearchScroll();
    this.exitMobileMainSearchMode();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    const wrapper = this.querySelector('.predictive-search__results-groups-wrapper');
    if (wrapper) {
      wrapper.style.removeProperty('max-height');
      wrapper.style.removeProperty('overflow-y');
      wrapper.style.removeProperty('overscroll-behavior');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
